import { useEffect, useRef, useState, useCallback } from "react";
import { io } from "socket.io-client";

import { map_settings } from "../../../../map";
import { createAnimatedMarker, createMarkerContent, loadScript } from "../../../../utils";
import { fetchFtmLiveMap } from "../../service";
import useWindowDimensions from "../../../../common/hooks/useWindowDimensions";

const SOCKET_SERVER_URL = "https://bookme-dev-sock.freshmilksoftware.com";

const Map = ({ jobLocation, isHome = false, step = 0 }) => {
    const mapRef = useRef(null);
    const markerRefs = useRef([]);
    const boundsRef = useRef(null);
    const socketRef = useRef(null);

    const [mapLoaded, setMapLoaded] = useState(false);
    const [liveMapDetails, setLiveMapDetails] = useState({
        loading: true,
        token: null,
        ftms: [],
    });

    const { width } = useWindowDimensions();

    const clearMarkers = useCallback(() => {
        markerRefs.current = markerRefs.current.filter(({ marker, type }) => {
            // keep CUSTOMER marker
            if (type === "CUSTOMER") {
                return true;
            }

            marker.setMap(null);
            return false;
        });
    }, []);

    const addMarkers = useCallback((map, icon, locations, type) => {
        if (!map || !boundsRef.current) return;

        locations.forEach(({ id, lat, lng }) => {
            const position = { lat, lng };

            const marker = new window.google.maps.marker.AdvancedMarkerElement({
                position,
                content:
                    type === "CUSTOMER"
                        ? (step === 6 || step === 7)
                            ? createAnimatedMarker(icon)
                            : createMarkerContent(icon)
                        : createMarkerContent(icon),
                map,
            });

            const existingIndex = markerRefs.current.findIndex((m) =>
                type === "FTM" ? m.ftm_id === id : m.type === type
            );

            if (existingIndex > -1) {
                markerRefs.current[existingIndex].marker.setMap(null);
                markerRefs.current[existingIndex].marker = marker;
            } else {
                markerRefs.current.push({
                    type,
                    ftm_id: id,
                    marker,
                });
            }

            boundsRef.current.extend(position);
        });

        map.fitBounds(boundsRef.current);
    }, [step]);

    useEffect(() => {
        loadScript(
            map_settings.map_script_url,
            () => setMapLoaded(true),
            console.error
        );
    }, []);

    useEffect(() => {
        if (!mapLoaded || !window.google?.maps) return;

        mapRef.current = new window.google.maps.Map(
            document.getElementById("map-container"),
            {
                center: { lat: 51.5286416, lng: -0.1015987 },
                zoom: 8,
                mapId: "CREATE_JOB_MAP",
            }
        );

        boundsRef.current = new window.google.maps.LatLngBounds();
    }, [mapLoaded]);

    useEffect(() => {
        if (!mapLoaded) return;

        const fetchData = async () => {
            try {
                const res = await fetchFtmLiveMap();

                const token = res?.details?.token || null;
                const ftms = res?.details?.ftms || [];

                setLiveMapDetails({
                    loading: false,
                    token,
                    ftms,
                });

                if (ftms.length) {
                    clearMarkers();

                    addMarkers(
                        mapRef.current,
                        map_settings.ftm_map_icon,
                        ftms.map((f) => ({
                            id: f.ftm_id,
                            lat: parseFloat(f.ftm_latitude),
                            lng: parseFloat(f.ftm_longitude),
                        })),
                        "FTM"
                    );
                }
            } catch (err) {
                console.error(err);
            }
        };

        fetchData();
    }, [mapLoaded, clearMarkers, addMarkers]);

    useEffect(() => {
        if (!mapLoaded || !liveMapDetails.token) return;

        socketRef.current = io(
            `${SOCKET_SERVER_URL}?token=${liveMapDetails.token}`
        );

        socketRef.current.on("connect", () =>
            console.log("Connected to socket server")
        );

        socketRef.current.on("live-locations", (payload) => {
            if (!mapRef.current) return;

            addMarkers(
                mapRef.current,
                map_settings.ftm_map_icon,
                [
                    {
                        id: payload.ftm_id,
                        lat: parseFloat(payload.ftm_latitude),
                        lng: parseFloat(payload.ftm_longitude),
                    },
                ],
                "FTM"
            );
        });

        return () => socketRef.current.disconnect();
    }, [mapLoaded, liveMapDetails.token, addMarkers]);

    // ✅ CUSTOMER marker
    useEffect(() => {
        if (!mapLoaded || !mapRef.current) return;

        const existingIndex = markerRefs.current.findIndex(
            (m) => m.type === "CUSTOMER"
        );

        // remove marker if no location
        if (!jobLocation?.location) {
            if (existingIndex > -1) {
                markerRefs.current[existingIndex].marker.setMap(null);
                markerRefs.current.splice(existingIndex, 1);
            }

            return;
        }

        const { lat, lng } = jobLocation.location;

        if (existingIndex > -1) {
            markerRefs.current[existingIndex].marker.setMap(null);
            markerRefs.current.splice(existingIndex, 1);
        }

        addMarkers(
            mapRef.current,
            map_settings.customer_map_icon,
            [
                {
                    id: "CUSTOMER",
                    lat,
                    lng,
                },
            ],
            "CUSTOMER"
        );
    }, [mapLoaded, jobLocation, addMarkers]);

    if (isHome) {
        return (
            <div
                className="card h-100 card-plain"
                style={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100vh",
                    width: "100%",
                }}
            >
                <div
                    id="map-container"
                    style={{
                        flexGrow: 1,
                        width: "100%",
                        height: width > 550 ? "100%" : "450px",
                    }}
                />
            </div>
        );
    }

    return (
        <div
            id="map-container"
            className="page-header bg-gradient-dark position-relative m-3 border-radius-xl min-vh-50"
        />
    );
};

export default Map;
